import React, { useContext, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import SidePanelHostProvider, {
  SidePanelUiContext,
} from '@/core/deep-dive/contexts/side-panel-provider';
import WidgetPane from '@/core/deep-dive/components/widget-pane';
import { getDeepDiveApp } from '@/core/deep-dive/services/app-registry';
import {
  announceDeepDiveWindowInstance,
  closeWindowSafely,
  getHostWindowId,
  subscribeDeepDiveWindowOpen,
} from '@/core/deep-dive/routing/deep-dive-window';
import styles from './deep-dive-window-page.module.css';

type NavigationProvider = React.ComponentType<React.PropsWithChildren>;

function DeepDiveAppWindowContent({
  instanceId,
  initialAppId,
  initialPath,
}: {
  instanceId: string;
  initialAppId: string;
  initialPath?: string;
}) {
  const sidePanelUi = useContext(SidePanelUiContext);
  const openedRef = useRef(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    sidePanelUi.openApp(initialAppId, false);
    if (initialPath) sidePanelUi.setWidgetPath(initialPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return subscribeDeepDiveWindowOpen(instanceId, (appId, path) => {
      sidePanelUi.openApp(appId, false);
      if (path) sidePanelUi.setWidgetPath(path);
    });
  }, [instanceId, sidePanelUi]);

  useEffect(() => {
    if (sidePanelUi.openPanelId === 'deep-dive') {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current) closeWindowSafely();
  }, [sidePanelUi.openPanelId]);

  const app = sidePanelUi.selectedAppId ? getDeepDiveApp(sidePanelUi.selectedAppId) : undefined;
  if (!app) return null;

  return (
    <div className={styles.window}>
      <div className={styles.appHeader}>
        <span>
          {app.icon} {app.label ?? app.defaultTitle}
        </span>
        <button
          type={'button'}
          className={'btn btn-sm text-muted'}
          onClick={() => sidePanelUi.closePanel('deep-dive')}
          title={'Close'}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      <div className={styles.contentBody}>
        <WidgetPane
          widget={app}
          panelId={'deep-dive'}
          path={sidePanelUi.widgetPath ?? undefined}
          onNavigate={path => sidePanelUi.setWidgetPath(path)}
        />
      </div>
    </div>
  );
}

function DeepDiveAppWindow({
  hostWindowId,
  instanceId,
  appId,
  path,
  navigationProvider: NavigationProvider,
}: {
  hostWindowId: string;
  instanceId: string;
  appId: string;
  path?: string;
  navigationProvider: NavigationProvider;
}) {
  useEffect(
    () => announceDeepDiveWindowInstance(hostWindowId, instanceId),
    [hostWindowId, instanceId],
  );

  return (
    <SidePanelHostProvider autoSelectFirstApp={false}>
      <NavigationProvider>
        <DeepDiveAppWindowContent instanceId={instanceId} initialAppId={appId} initialPath={path} />
      </NavigationProvider>
    </SidePanelHostProvider>
  );
}

/**
 * `navigationProvider` is the app's widget-navigation provider. It is passed in by the
 * composition root because widget navigation sits above deep-dive and dispatches into it.
 */
export default function DeepDiveWindowPage({
  navigationProvider,
}: {
  navigationProvider: NavigationProvider;
}) {
  const [params] = useState(() => new URLSearchParams(window.location.search));
  const appId = params.get('app');
  const path = params.get('path') ?? undefined;
  const [instanceId] = useState(() => params.get('instance') ?? crypto.randomUUID());
  const [hostWindowId] = useState(() => params.get('host') ?? getHostWindowId());

  if (!appId) {
    return (
      <div className={styles.window}>
        <div className={'text-center text-muted mt-5 small'}>
          Nothing to show — no app specified.
        </div>
      </div>
    );
  }

  return (
    <DeepDiveAppWindow
      hostWindowId={hostWindowId}
      instanceId={instanceId}
      appId={appId}
      path={path}
      navigationProvider={navigationProvider}
    />
  );
}
